"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
exports.__esModule = true;
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutPropertiesLoose2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutPropertiesLoose"));
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _styles = _interopRequireDefault(require("../styles"));
var _Tooltip = _interopRequireDefault(require("@mui/material/Tooltip"));
var _EDSContext = require("../EDSContext/EDSContext");
var _TooltipErrorBoundry = _interopRequireDefault(require("./Internal/TooltipErrorBoundry"));
var _tokens = require("../styles/tokens");
var _isNil = _interopRequireDefault(require("lodash/isNil"));
var _excluded = ["classes", "edsContext", "PopperProps", "open", "onOpen", "onClose"];
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function _getRequireWildcardCache(e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
var styles = function styles(theme) {
  var _tooltip;
  return {
    tooltip: (_tooltip = {
      paddingLeft: _tokens.spacing40,
      paddingRight: _tokens.spacing40,
      fontSize: theme.typography.body2.fontSize,
      maxWidth: '18.750rem',
      lineHeight: theme.typography.caption.lineHeight,
      textAlign: 'center',
      backgroundColor: theme.palette.grey[500]
    }, _tooltip[theme.breakpoints.up('sm')] = {
      fontSize: theme.typography.caption.fontSize,
      padding: _tokens.spacing20
    }, _tooltip)
  };
};

/**
 * Use `Tooltip` to display a small amount of informative text.
 *
 * @done true
 * @updated v7.8.0
 * @versionAdded v0.0.9
 * @examples
 *  TooltipExample
 *  TooltipPositionedExample
 *  TooltipDisabledButton
 */
var Tooltip = /*#__PURE__*/_react.default.forwardRef(function (props, ref) {
  var classes = props.classes,
    edsContext = props.edsContext,
    PopperProps = props.PopperProps,
    openProp = props.open,
    onOpenProp = props.onOpen,
    onCloseProp = props.onClose,
    rest = (0, _objectWithoutPropertiesLoose2.default)(props, _excluded);
  var classOverrides = {
    tooltip: classes.tooltip
  };

  // maintain open state of tooltip (if tooltip is uncontrolled)
  var _useState = (0, _react.useState)(false),
    open = _useState[0],
    setOpen = _useState[1];
  var popperPropOverrides = (0, _extends2.default)({}, {
    dir: edsContext.dir
  }, PopperProps);

  // close tooltip when escape is pressed
  var escapeDetector = (0, _react.useCallback)(function (e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      // controlled
      if (onCloseProp) {
        onCloseProp(e);

        // uncontrolled
      } else {
        setOpen(false);
      }
    }
  }, [onCloseProp, setOpen]);

  // register keyboard handler to detect escape key press when tooltip is opened
  (0, _react.useEffect)(function () {
    if (open || openProp) {
      document.addEventListener('keydown', escapeDetector);
    }
    return function () {
      document.removeEventListener('keydown', escapeDetector);
    };
  }, [open, openProp, escapeDetector]);
  return /*#__PURE__*/_react.default.createElement(_TooltipErrorBoundry.default, null, /*#__PURE__*/_react.default.createElement(_Tooltip.default, (0, _extends2.default)({
    PopperProps: popperPropOverrides,
    classes: classOverrides,
    ref: ref,
    onOpen: (0, _isNil.default)(onOpenProp) ? function () {
      return setOpen(true);
    } : onOpenProp,
    onClose: (0, _isNil.default)(onCloseProp) ? function () {
      return setOpen(false);
    } : onCloseProp,
    open: (0, _isNil.default)(openProp) ? open : openProp
  }, rest)));
});
Tooltip.muiName = 'Tooltip';
Tooltip.displayName = 'Tooltip';
Tooltip.propTypes = {
  /**
   * Tooltip reference element.
   */
  children: _propTypes.default.element.isRequired,
  /**
   * Override or extend the styles applied to the component.
   */
  classes: _propTypes.default.object,
  /**
   * @ignore
   */
  className: _propTypes.default.string,
  /**
   * Do not respond to focus events.
   */
  disableFocusListener: _propTypes.default.bool,
  /**
   * Do not respond to hover events.
   */
  disableHoverListener: _propTypes.default.bool,
  /**
   * Do not respond to long press touch events.
   */
  disableTouchListener: _propTypes.default.bool,
  /**
   * The number of milliseconds to wait before showing the tooltip.
   * This property won't impact the enter touch delay (`enterTouchDelay`).
   */
  enterDelay: _propTypes.default.number,
  /**
   * The number of milliseconds to wait before showing the tooltip after one was already recently opened.
   */
  enterNextDelay: _propTypes.default.number,
  /**
   * The number of milliseconds a user must touch the element before showing the tooltip.
   */
  enterTouchDelay: _propTypes.default.number,
  /**
   * The relationship between the tooltip and the wrapper component is not clear from the DOM.
   * By providing this property, we can use aria-describedby to solve the accessibility issue.
   */
  id: _propTypes.default.string,
  /**
   * The number of milliseconds to wait before hiding the tooltip.
   * This property won't impact the leave touch delay (`leaveTouchDelay`).
   */
  leaveDelay: _propTypes.default.number,
  /**
   * The number of milliseconds after the user stops touching an element before hiding the tooltip.
   */
  leaveTouchDelay: _propTypes.default.number,
  /**
   * Callback fired when the tooltip requests to be closed.
   *
   * @param {object} event The event source of the callback
   */
  onClose: _propTypes.default.func,
  /**
   * Callback fired when the tooltip requests to be open.
   *
   * @param {object} event The event source of the callback
   */
  onOpen: _propTypes.default.func,
  /**
   * If `true`, the tooltip is shown.
   */
  open: _propTypes.default.bool,
  /**
   * Tooltip placement.
   *
   * When using values besides "top" or "bottom", be sure to evaluate the Tooltip direction when your app is RTL.
   */
  placement: _propTypes.default.oneOf(['bottom-end', 'bottom-start', 'bottom', 'left-end', 'left-start', 'left', 'right-end', 'right-start', 'right', 'top-end', 'top-start', 'top']),
  /**
   * Properties applied to the `Popper` element.
   */
  PopperProps: _propTypes.default.object,
  /**
   * @ignore
   */
  theme: _propTypes.default.object,
  /**
   * Tooltip title. Zero-length titles string are never displayed.
   */
  title: _propTypes.default.node.isRequired,
  /**
   * @ignore
   */
  edsContext: _propTypes.default.object
};

// If default props are needed, they must be set here
Tooltip.defaultProps = {
  enterDelay: 500,
  enterTouchDelay: 700,
  leaveDelay: 0,
  leaveTouchDelay: 1500,
  disableFocusListener: false,
  disableHoverListener: false,
  disableTouchListener: false,
  placement: 'bottom'
};
var _default = exports.default = (0, _EDSContext.withEDSContext)((0, _styles.default)(styles)(Tooltip));